import { useCallback, useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const NoResults = [];

/**
 * @template T
 * @param {import("@tanstack/react-query").UseQueryResult<T[]>} query
 * @param {Filter<T>[]} allFilters
 * @param {string} param when loading filter from query string, what key
 * @returns {Filters<T>}
 */
export const useFiltersv2 = (query, allFilters, param) => {
  const { data: allItems = NoResults, isLoading } = query;

  /**
   * @type {[T[],import('react').Dispatch<import('react').SetStateAction<T[]>>]}
   */
  const [filtered, setFiltered] = useState(allItems);

  const [searchParams, setSearchParams] = useSearchParams();
  const [done, setDone] = useState(false);
  const [filter, setFilter] = useState(`${searchParams.get(param)}`.toUpperCase());
  const [visibleFilters, setVisibleFilters] = useState(/** @type {Filter<T>[]} */ ([]));

  // Sync filter from URL when user navigates (e.g. from DoneGamesListAndDetail to another filter)
  useEffect(() => {
    const fromUrl = searchParams.get(param);
    const normalized = fromUrl ? `${fromUrl}`.toUpperCase() : "";
    if (normalized && normalized !== filter) {
      setFilter(normalized);
    }
  }, [searchParams, param]);

  /**
   * @param {string} label
   */
  const onFilterClicked = useCallback(
    (label) => {
      searchParams.set(param, label);
      setSearchParams(searchParams);
      setFilter(label);
    },
    [param, searchParams, setSearchParams]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (query.isError) {
      return;
    }
    if (allItems.length === 0) {
      return;
    }

    const appliedFilter = allFilters.find(({ label }) => label === filter);
    if (!appliedFilter) {
      setFiltered(allItems);
      return;
    }

    const toShow = allItems.filter((t) => appliedFilter.filter(t));
    if (toShow.length === 0) {
      setFilter("ALL");
      setFiltered(allItems);
      searchParams.set(param, "ALL");
      return;
    }

    setFiltered(toShow);
  }, [allItems, isLoading, filter, allFilters, param, searchParams, query.isError]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (query.isError) {
      return;
    }
    if (allFilters.length === 0) {
      return;
    }

    const withCount = allFilters
      .map((filter) => ({
        ...filter,
        count: allItems.filter((t) => filter.filter(t)).length,
      }))
      .filter(({ count, label }) => count > 0 || label === "ALL")
      .filter(({ count, label }) => count !== allItems.length || label === "ALL");

    setVisibleFilters(withCount);

    if (done && !!searchParams.get(param)) {
      return;
    }
    if (done && !searchParams.get(param)) {
      const sorted = [...withCount];
      sorted.sort((a, b) => b.weight - a.weight);

      setFilter(sorted[0].label);
      searchParams.set(param, sorted[0].label);
      setSearchParams(searchParams, { replace: true });
    }

    if (!done && allItems.length > 0) {
      const sorted = [...withCount];
      sorted.sort((a, b) => b.weight - a.weight);

      if (!searchParams.get(param)) {
        setFilter(sorted[0].label);
        searchParams.set(param, sorted[0].label);
        setSearchParams(searchParams, { replace: true });
      }
      setDone(true);
    }
  }, [allItems, isLoading, query.isError, done, searchParams, param, allFilters, setSearchParams]);

  const value = useMemo(
    () => ({
      filtered,
      filter,
      visibleFilters: visibleFilters.length > 1 ? visibleFilters : NoResults,
      onFilterClicked,
    }),
    [filtered, filter, onFilterClicked, visibleFilters]
  );

  return value;
};
